import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { subApi, setSubToken, setSubContext, getSubContext } from "@/lib/subApi";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2, Search, MapPin, Calendar, Image as ImageIcon, ArrowLeft, Download, LogOut, Map as MapIcon, LayoutGrid, FileSignature, CheckCircle2, Bell } from "lucide-react";
import NotifPrefsPanel from "@/components/NotifPrefsPanel";
import PhotoLightbox from "@/components/PhotoLightbox";
import { nombreFotoBase } from "@/lib/fotoUtils";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix default Leaflet marker icons (Webpack no resuelve las URLs por defecto)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// ============ LOGIN ============
export function PortalSubLogin() {
  const [search] = useSearchParams();
  const slug = search.get("slug") || "";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [brand, setBrand] = useState({ nombre: "Portal", color_primario: "#f59e0b", logo_url: null });
  const nav = useNavigate();

  useEffect(() => {
    // Si ya hay sesión, redirige
    const ctx = getSubContext();
    if (ctx?.sub_cliente) nav("/portal-sub/dashboard", { replace: true });
    // Cargar branding público según slug para que el login ya luzca la marca
    if (slug) {
      subApi.get(`/portal-sub/brand/${slug}`)
        .then((r) => setBrand({ ...brand, ...r.data }))
        .catch(() => { /* slug desconocido: deja branding default */ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nav, slug]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await subApi.post("/portal-sub/login", { email, password });
      setSubToken(data.access_token);
      setSubContext({ user: data.user, sub_cliente: data.sub_cliente });
      setBrand(data.sub_cliente);
      toast.success(`Bienvenido a ${data.sub_cliente.nombre}`);
      nav("/portal-sub/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Credenciales inválidas");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0a0c] text-zinc-100 p-6"
      data-testid="portal-sub-login">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          {brand.logo_url ? (
            <img src={brand.logo_url} alt={brand.nombre}
              className="h-16 max-w-[260px] mx-auto mb-4 object-contain" />
          ) : (
            <div className="w-20 h-20 rounded-2xl mx-auto mb-3 flex items-center justify-center text-2xl font-bold"
              style={{ background: brand.color_primario, color: "#0a0a0c" }}>
              {(slug || "P").slice(0, 1).toUpperCase()}
            </div>
          )}
          <h1 className="text-2xl font-bold tracking-tight">{brand.nombre !== "Portal" ? brand.nombre : "Portal de cliente"}</h1>
          <p className="text-sm text-zinc-500 mt-1">Acceso restringido</p>
        </div>
        <form onSubmit={submit} className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 space-y-3">
          <div>
            <Label className="text-xs text-zinc-400">Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              autoFocus required className="bg-zinc-950 border-zinc-800" data-testid="sub-login-email" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Contraseña</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              required className="bg-zinc-950 border-zinc-800" data-testid="sub-login-password" />
          </div>
          <Button type="submit" disabled={loading} className="w-full"
            style={{ background: brand.color_primario, color: "#0a0a0c" }} data-testid="sub-login-submit">
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Accediendo…</> : "Entrar"}
          </Button>
        </form>
        <div className="text-center text-[11px] text-zinc-500 mt-6">
          powered by <span className="text-amber-400 font-medium">RotulaciónDNF</span>
        </div>
      </div>
    </div>
  );
}


// ============ SHELL (layout shared post-login) ============
function PortalSubShell({ children }) {
  const ctx = getSubContext();
  const nav = useNavigate();
  useEffect(() => { if (!ctx?.sub_cliente) nav("/portal-sub/login", { replace: true }); }, [ctx, nav]);
  if (!ctx?.sub_cliente) return null;
  const sc = ctx.sub_cliente;
  const logout = async () => {
    try { await subApi.post("/portal-sub/logout"); } catch { /* ignore */ }
    setSubToken(null); setSubContext(null);
    nav("/portal-sub/login");
  };

  return (
    <div className="min-h-screen bg-[#0a0a0c] text-zinc-100">
      <header className="border-b border-zinc-800/80 bg-[#0c0c10] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/portal-sub/dashboard" className="flex items-center gap-3 group">
            {sc.logo_url ? (
              <img src={sc.logo_url} alt={sc.nombre}
                className="h-9 max-w-[140px] object-contain rounded bg-white/0" />
            ) : (
              <div className="w-9 h-9 rounded-lg flex items-center justify-center text-sm font-bold"
                style={{ background: sc.color_primario, color: "#0a0a0c" }}>
                {sc.nombre.slice(0, 1)}
              </div>
            )}
            <div>
              <div className="text-sm font-bold group-hover:text-amber-300 transition-colors">{sc.nombre}</div>
              <div className="text-[10px] text-zinc-500 uppercase tracking-[0.18em]">Reportes de montaje</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/portal-sub/partes" className="text-xs text-zinc-400 hover:text-amber-300 transition-colors hidden md:flex items-center gap-1.5" data-testid="link-partes">
              <FileSignature className="w-3.5 h-3.5" /> Mis partes firmados
            </Link>
            <Link to="/portal-sub/notificaciones" className="text-xs text-zinc-400 hover:text-amber-300 transition-colors hidden md:flex items-center gap-1.5" data-testid="link-notif-prefs">
              <Bell className="w-3.5 h-3.5" /> Notificaciones
            </Link>
            <div className="text-right">
              <div className="text-xs text-zinc-300">{ctx.user.nombre}</div>
              <div className="text-[10px] text-zinc-500">{ctx.user.email}</div>
            </div>
            <Button size="sm" variant="outline" onClick={logout}
              className="bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-900" data-testid="sub-logout">
              <LogOut className="w-3.5 h-3.5 mr-1.5" /> Salir
            </Button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-6 py-6">{children}</main>
      <footer className="text-center text-[10px] text-zinc-500 py-6">
        powered by <span className="text-amber-400">RotulaciónDNF</span>
      </footer>
    </div>
  );
}


// ============ DASHBOARD ============
export function PortalSubDashboard() {
  const [espacios, setEspacios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cadenaFilter, setCadenaFilter] = useState("");
  const [viewMode, setViewMode] = useState("grid");  // grid | map

  useEffect(() => {
    subApi.get("/portal-sub/espacios")
      .then(({ data }) => setEspacios(data || []))
      .catch(() => toast.error("Error cargando espacios"))
      .finally(() => setLoading(false));
  }, []);

  const cadenas = useMemo(() => {
    const set = new Set(espacios.map((e) => e.cadena_nombre || e.cadena).filter(Boolean));
    return Array.from(set).sort();
  }, [espacios]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return espacios.filter((e) => {
      if (cadenaFilter && (e.cadena_nombre || e.cadena) !== cadenaFilter) return false;
      if (!ql) return true;
      return (
        (e.nombre || "").toLowerCase().includes(ql) ||
        (e.ciudad || "").toLowerCase().includes(ql) ||
        (e.provincia || "").toLowerCase().includes(ql) ||
        (e.direccion || "").toLowerCase().includes(ql)
      );
    });
  }, [espacios, q, cadenaFilter]);

  const totalMontajes = espacios.reduce((s, e) => s + (e._montajes_validados || 0), 0);
  const conActividad = espacios.filter((e) => (e._montajes_validados || 0) > 0).length;

  return (
    <PortalSubShell>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="sub-kpis">
        <KPI label="Espacios" value={espacios.length} />
        <KPI label="Espacios con montajes" value={conActividad} accent="emerald" />
        <KPI label="Montajes validados" value={totalMontajes} accent="amber" />
        <KPI label="Cadenas" value={cadenas.length} accent="violet" />
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-3 mb-4 items-end" data-testid="sub-filters">
        <div className="flex-1 min-w-[240px]">
          <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Buscar</Label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-zinc-500" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Nombre, ciudad, provincia, dirección…"
              className="bg-zinc-900 border-zinc-800 pl-8" data-testid="sub-search" />
          </div>
        </div>
        {cadenas.length > 0 && (
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Cadena</Label>
            <select value={cadenaFilter} onChange={(e) => setCadenaFilter(e.target.value)}
              className="bg-zinc-900 border border-zinc-800 rounded h-10 px-3 text-sm text-zinc-100" data-testid="sub-cadena">
              <option value="">Todas</option>
              {cadenas.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        )}
        <div className="inline-flex bg-zinc-900 border border-zinc-800 rounded overflow-hidden h-10" data-testid="sub-view-toggle">
          <button onClick={() => setViewMode("grid")}
            className={`px-3 flex items-center gap-1.5 text-xs uppercase tracking-wider ${
              viewMode === "grid" ? "bg-amber-500 text-zinc-950" : "text-zinc-400 hover:text-zinc-100"}`}
            data-testid="sub-view-grid">
            <LayoutGrid className="w-3.5 h-3.5" /> Lista
          </button>
          <button onClick={() => setViewMode("map")}
            className={`px-3 flex items-center gap-1.5 text-xs uppercase tracking-wider border-l border-zinc-800 ${
              viewMode === "map" ? "bg-amber-500 text-zinc-950" : "text-zinc-400 hover:text-zinc-100"}`}
            data-testid="sub-view-map">
            <MapIcon className="w-3.5 h-3.5" /> Mapa
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-zinc-500 bg-zinc-900/30 border border-zinc-800 rounded-lg">
          {espacios.length === 0
            ? "Aún no hay espacios asignados a tu acceso. Contacta con tu responsable."
            : "Ningún espacio coincide con tu búsqueda."}
        </div>
      ) : viewMode === "map" ? (
        <EspaciosMap espacios={filtered} />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="sub-espacios-grid">
          {filtered.map((e) => (
            <Link key={e.id} to={`/portal-sub/espacios/${e.id}`}
              className="group bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden hover:border-amber-500/50 hover:bg-zinc-900 transition-all"
              data-testid={`espacio-card-${e.id}`}>
              <div className="aspect-video bg-zinc-950 overflow-hidden relative">
                {e._thumbnail ? (
                  <img src={e._thumbnail} alt={e.nombre}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-zinc-700">
                    <ImageIcon className="w-12 h-12" />
                  </div>
                )}
                {(e._montajes_validados || 0) > 0 && (
                  <div className="absolute top-2 right-2 bg-emerald-500 text-zinc-950 text-[10px] font-bold rounded-full px-2 py-0.5">
                    {e._montajes_validados} montaje{e._montajes_validados !== 1 ? "s" : ""}
                  </div>
                )}
              </div>
              <div className="p-3">
                <div className="font-medium text-zinc-100 truncate group-hover:text-amber-300 transition-colors">{e.nombre}</div>
                <div className="text-xs text-zinc-500 flex items-center gap-1 mt-0.5">
                  <MapPin className="w-3 h-3" /> {e.ciudad || "—"}{e.provincia ? `, ${e.provincia}` : ""}
                </div>
                {e._ultima_validacion && (
                  <div className="text-[10px] text-zinc-500 mt-1.5 flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> Última campaña: {e._ultima_validacion.slice(0, 10)}
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </PortalSubShell>
  );
}


// ============ ESPACIO DETAIL ============
export function PortalSubEspacio() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [lightbox, setLightbox] = useState(null);  // {url, alt, all:[]}
  const [compareMode, setCompareMode] = useState(false);
  const [compareIds, setCompareIds] = useState([]);  // [id_a, id_b]
  const [compareData, setCompareData] = useState(null);
  const [zipLoading, setZipLoading] = useState(false);

  const load = (params = {}) => {
    setLoading(true);
    subApi.get(`/portal-sub/espacios/${id}/montajes`, { params })
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Error cargando montajes"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const aplicarFiltro = () => load({ desde: desde || undefined, hasta: hasta || undefined });
  const limpiarFiltro = () => { setDesde(""); setHasta(""); load(); };

  const descargarZip = async () => {
    setZipLoading(true);
    try {
      const params = new URLSearchParams();
      if (desde) params.set("desde", desde);
      if (hasta) params.set("hasta", hasta);
      const token = localStorage.getItem("dnf_sub_token") || "";
      const url = `${subApi.defaults.baseURL}/portal-sub/espacios/${id}/descargar-zip?${params.toString()}`;
      const r = await fetch(url, { headers: { "X-Sub-Token": token } });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${data?.espacio?.nombre || "fotos"}.zip`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("ZIP descargado");
    } catch (err) { toast.error(`Error descargando ZIP: ${err.message}`); }
    finally { setZipLoading(false); }
  };

  const toggleCompare = (lineaId) => {
    if (compareIds.includes(lineaId)) {
      setCompareIds(compareIds.filter((x) => x !== lineaId));
    } else if (compareIds.length < 2) {
      setCompareIds([...compareIds, lineaId]);
    } else {
      toast.info("Sólo puedes comparar 2 campañas. Quita una primero.");
    }
  };

  const lanzarComparador = async () => {
    if (compareIds.length !== 2) return;
    try {
      const { data } = await subApi.get(
        `/portal-sub/espacios/${id}/comparar`,
        { params: { linea_a: compareIds[0], linea_b: compareIds[1] } },
      );
      setCompareData(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  return (
    <PortalSubShell>
      <Link to="/portal-sub/dashboard"
        className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-amber-300 mb-3" data-testid="sub-back">
        <ArrowLeft className="w-3.5 h-3.5" /> Volver al listado
      </Link>

      {data?.espacio && (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5 mb-4" data-testid="sub-espacio-hdr">
          <h1 className="text-xl font-bold tracking-tight">{data.espacio.nombre}</h1>
          <div className="text-sm text-zinc-400 flex items-center gap-1.5 mt-1">
            <MapPin className="w-3.5 h-3.5" />
            {data.espacio.direccion}, {data.espacio.ciudad}{data.espacio.provincia ? `, ${data.espacio.provincia}` : ""}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3 mb-4" data-testid="sub-periodo">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Desde</Label>
          <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)}
            className="bg-zinc-900 border-zinc-800 w-44 h-10" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Hasta</Label>
          <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)}
            className="bg-zinc-900 border-zinc-800 w-44 h-10" />
        </div>
        <Button onClick={aplicarFiltro} className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="btn-filtrar">
          Filtrar
        </Button>
        {(desde || hasta) && (
          <Button onClick={limpiarFiltro} variant="ghost" className="text-zinc-400 hover:text-zinc-100">Limpiar</Button>
        )}
        <div className="flex-1" />
        {data?.montajes?.length >= 2 && (
          <Button onClick={() => { setCompareMode(!compareMode); setCompareIds([]); }}
            variant="outline"
            className={`bg-transparent border-violet-500/40 ${compareMode ? "bg-violet-500/20 text-violet-200" : "text-violet-300"} hover:bg-violet-500/10`}
            data-testid="btn-compare-mode">
            {compareMode ? "✕ Cancelar comparar" : "⇆ Comparar 2 campañas"}
          </Button>
        )}
        {data?.montajes?.length > 0 && (
          <Button onClick={descargarZip} disabled={zipLoading} variant="outline"
            className="bg-transparent border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
            data-testid="btn-descargar-zip">
            {zipLoading
              ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Generando ZIP…</>
              : <><Download className="w-3.5 h-3.5 mr-1.5" /> Descargar ZIP</>}
          </Button>
        )}
      </div>

      {compareMode && (
        <div className="bg-violet-500/10 border border-violet-500/30 rounded-lg p-3 mb-4 flex items-center gap-3"
          data-testid="compare-bar">
          <div className="text-sm text-violet-100">
            <b>Modo comparador:</b> selecciona 2 campañas para ver lado-a-lado
            ({compareIds.length}/2)
          </div>
          <div className="flex-1" />
          <Button size="sm" disabled={compareIds.length !== 2} onClick={lanzarComparador}
            className="bg-violet-500 text-zinc-950 hover:bg-violet-400 disabled:opacity-40"
            data-testid="btn-launch-compare">
            Comparar →
          </Button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : !data?.montajes?.length ? (
        <div className="text-center py-12 text-zinc-500 bg-zinc-900/30 border border-zinc-800 rounded-lg">
          No hay montajes validados en este periodo.
        </div>
      ) : (
        <div className="space-y-6" data-testid="sub-montajes">
          {data.montajes.map((m) => {
            const isSelected = compareIds.includes(m.id);
            return (
              <div key={m.id}
                onClick={compareMode ? () => toggleCompare(m.id) : undefined}
                className={`bg-zinc-900/40 border rounded-xl p-4 transition-all ${
                  compareMode
                    ? "cursor-pointer hover:border-violet-400 "
                      + (isSelected ? "border-violet-400 ring-2 ring-violet-500/40 bg-violet-500/5" : "border-zinc-800")
                    : "border-zinc-800"
                }`}
                data-testid={`montaje-${m.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {compareMode && (
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center text-[10px] font-bold ${
                        isSelected ? "bg-violet-500 border-violet-500 text-zinc-950" : "border-zinc-600"
                      }`}>
                        {isSelected ? "✓" : ""}
                      </div>
                    )}
                    <div>
                      <div className="font-semibold text-zinc-100">{m.peticion_nombre || "Campaña"}</div>
                      <div className="text-xs text-zinc-500 font-mono">
                        {m.peticion_codigo} · {m.finalizado_at?.slice(0, 10)} · {m.n_fotos} foto{m.n_fotos !== 1 ? "s" : ""}
                      </div>
                    </div>
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded px-2 py-0.5">
                    Validado
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {(m.fotos_finales || []).map((f, idx) => (
                    <button key={idx}
                      onClick={(e) => { if (compareMode) return; e.stopPropagation(); setLightbox({ fotos: m.fotos_finales || [], idx, nameBase: nombreFotoBase(data?.espacio?.nombre, m.marca) }); }}
                      className={`aspect-square bg-zinc-950 rounded overflow-hidden ${compareMode ? "" : "hover:ring-2 hover:ring-amber-400"} transition-all`}
                      data-testid={`thumb-${m.id}-${idx}`}>
                      <img src={f.url} alt={f.categoria || ""} className="w-full h-full object-cover" loading="lazy" />
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {compareData && <CompareDialog data={compareData} onClose={() => setCompareData(null)} />}

      {lightbox && (
        <PhotoLightbox
          fotos={lightbox.fotos}
          openIdx={lightbox.idx}
          onClose={() => setLightbox(null)}
          nameForIdx={(i) => `${lightbox.nameBase}_${String(i + 1).padStart(2, "0")}`}
        />
      )}
    </PortalSubShell>
  );
}


function CompareDialog({ data, onClose }) {
  const { campania_a, campania_b, espacio } = data;
  const maxFotos = Math.max(campania_a.fotos_finales.length, campania_b.fotos_finales.length);
  return (
    <div onClick={onClose}
      className="fixed inset-0 z-50 bg-black/95 overflow-y-auto p-4 cursor-zoom-out"
      data-testid="compare-dialog">
      <div onClick={(e) => e.stopPropagation()} className="max-w-7xl mx-auto cursor-default">
        <div className="flex items-center justify-between mb-4 sticky top-0 bg-[#0a0a0c]/95 backdrop-blur py-3 z-10">
          <div>
            <h2 className="text-lg font-bold text-zinc-100">Comparador de campañas</h2>
            <div className="text-xs text-zinc-500">{espacio?.nombre} · {espacio?.ciudad}</div>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-100 px-3 py-1 border border-zinc-700 rounded text-sm">
            ✕ Cerrar
          </button>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          {[campania_a, campania_b].map((c, i) => (
            <div key={i} className={`bg-zinc-900/60 border ${i === 0 ? "border-amber-500/40" : "border-emerald-500/40"} rounded-lg p-3`}>
              <div className={`text-[10px] uppercase tracking-wider ${i === 0 ? "text-amber-300" : "text-emerald-300"} mb-1`}>
                {i === 0 ? "Campaña A" : "Campaña B"}
              </div>
              <div className="text-sm font-semibold text-zinc-100">{c.peticion_nombre}</div>
              <div className="text-[11px] text-zinc-500 font-mono">{c.peticion_codigo} · {c.finalizado_at?.slice(0, 10)} · {c.n_fotos} foto(s)</div>
            </div>
          ))}
        </div>

        {/* Side-by-side fotos */}
        <div className="space-y-3">
          {Array.from({ length: maxFotos }).map((_, idx) => (
            <div key={idx} className="grid grid-cols-2 gap-3">
              <div className="aspect-video bg-zinc-950 rounded overflow-hidden border border-amber-500/20">
                {campania_a.fotos_finales[idx] ? (
                  <img src={campania_a.fotos_finales[idx].url}
                    alt="" className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-zinc-700 text-xs">—</div>
                )}
              </div>
              <div className="aspect-video bg-zinc-950 rounded overflow-hidden border border-emerald-500/20">
                {campania_b.fotos_finales[idx] ? (
                  <img src={campania_b.fotos_finales[idx].url}
                    alt="" className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-zinc-700 text-xs">—</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


function EspaciosMap({ espacios }) {
  const conCoords = espacios.filter((e) => typeof e.lat === "number" && typeof e.lng === "number");
  // Centro: media de coords, o Madrid si no hay
  const centro = useMemo(() => {
    if (conCoords.length === 0) return [40.42, -3.7];
    const lat = conCoords.reduce((s, e) => s + e.lat, 0) / conCoords.length;
    const lng = conCoords.reduce((s, e) => s + e.lng, 0) / conCoords.length;
    return [lat, lng];
  }, [conCoords]);
  const sinCoords = espacios.length - conCoords.length;

  return (
    <div data-testid="sub-espacios-map">
      <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl overflow-hidden" style={{ height: 560 }}>
        <MapContainer center={centro} zoom={conCoords.length > 1 ? 6 : 12}
          style={{ height: "100%", width: "100%", background: "#f3f4f6" }}
          scrollWheelZoom>
          <TileLayer
            attribution='&copy; <a href="https://carto.com/">CARTO</a> &copy; OpenStreetMap'
            url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
            maxZoom={20}
          />
          {conCoords.map((e) => (
            <Marker key={e.id} position={[e.lat, e.lng]}>
              <Popup>
                <div style={{ minWidth: 220 }}>
                  {e._thumbnail && (
                    <img src={e._thumbnail} alt=""
                      style={{ width: "100%", height: 110, objectFit: "cover", borderRadius: 4, marginBottom: 6 }} />
                  )}
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{e.nombre}</div>
                  <div style={{ fontSize: 11, color: "#666" }}>
                    {e.ciudad}{e.provincia ? `, ${e.provincia}` : ""}
                  </div>
                  {(e._montajes_validados || 0) > 0 && (
                    <div style={{ fontSize: 11, color: "#0a7a3b", fontWeight: 600, marginTop: 4 }}>
                      {e._montajes_validados} montaje(s) validado(s)
                    </div>
                  )}
                  <Link to={`/portal-sub/espacios/${e.id}`}
                    style={{ display: "inline-block", marginTop: 8, background: "#f59e0b", color: "#0a0a0c",
                              padding: "4px 10px", borderRadius: 4, fontSize: 11, fontWeight: 600,
                              textDecoration: "none" }}>
                    Ver campañas →
                  </Link>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
      {sinCoords > 0 && (
        <div className="text-[10px] text-zinc-500 mt-2">
          ℹ️ {sinCoords} espacio(s) sin coordenadas — sólo visibles en la vista Lista.
        </div>
      )}
    </div>
  );
}


function KPI({ label, value, accent = "zinc" }) {  const cls = {
    zinc: "border-zinc-700/50 text-zinc-100",
    amber: "border-amber-500/40 text-amber-200",
    emerald: "border-emerald-500/40 text-emerald-200",
    violet: "border-violet-500/40 text-violet-200",
  }[accent];
  return (
    <div className={`bg-zinc-900/50 border rounded-lg p-3 ${cls}`}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}



// ============ PARTES FIRMADOS ============
export function PortalSubPartes() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const nav = useNavigate();
  const ctx = getSubContext();

  useEffect(() => {
    if (!ctx?.sub_cliente) {
      nav("/portal-sub/login", { replace: true });
      return;
    }
    subApi.get("/portal-sub/partes-firmados")
      .then(({ data }) => setItems(data.items || []))
      .catch(() => toast.error("Error cargando partes"))
      .finally(() => setLoading(false));
  // eslint-disable-next-line
  }, []);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return items;
    return items.filter((p) =>
      (p.codigo || "").toLowerCase().includes(ql) ||
      (p.nombre || "").toLowerCase().includes(ql) ||
      (p.direccion || "").toLowerCase().includes(ql) ||
      (p.firmado_por || "").toLowerCase().includes(ql)
    );
  }, [items, q]);

  if (loading) {
    return <PortalSubShell><div className="flex justify-center p-10"><Loader2 className="w-8 h-8 animate-spin text-amber-400" /></div></PortalSubShell>;
  }

  return (
    <PortalSubShell>
      <div className="mb-6">
        <Link to="/portal-sub/dashboard" className="text-xs text-zinc-500 hover:text-amber-300 inline-flex items-center gap-1 mb-2">
          <ArrowLeft className="w-3 h-3" /> Volver al dashboard
        </Link>
        <h1 className="text-2xl font-bold text-zinc-100 flex items-center gap-2">
          <FileSignature className="w-6 h-6 text-amber-400" /> Mis partes firmados
        </h1>
        <p className="text-sm text-zinc-400 mt-1">Documentos de conformidad firmados digitalmente tras cada montaje completado.</p>
      </div>

      <div className="mb-4">
        <div className="relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por código, dirección, firmante..."
            className="bg-zinc-900/50 border-zinc-700 pl-9"
            data-testid="search-partes"
          />
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-zinc-900/30 border border-zinc-800 rounded-xl p-12 text-center" data-testid="empty-partes">
          <FileSignature className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
          <div className="text-zinc-400 text-sm">
            {q ? "Sin resultados para tu búsqueda." : "Aún no hay partes firmados disponibles."}
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((p) => (
            <div key={p.peticion_id} className="bg-zinc-900/40 border border-zinc-800 rounded-lg p-4 hover:border-emerald-500/30 transition" data-testid={`parte-row-${p.peticion_id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 fill-emerald-400/20 shrink-0" />
                    <span className="font-mono text-xs text-amber-400">{p.codigo}</span>
                    {p.firmado_via === "presencial" && <span className="px-1.5 py-0.5 rounded text-[10px] bg-blue-500/20 text-blue-300">📲 In-situ</span>}
                    {p.firmado_via === "portal" && <span className="px-1.5 py-0.5 rounded text-[10px] bg-violet-500/20 text-violet-300">🌐 Remoto</span>}
                  </div>
                  <div className="text-sm text-zinc-200 font-semibold truncate">{p.nombre || p.espacio_nombre || "—"}</div>
                  {p.direccion && <div className="text-xs text-zinc-500 truncate flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" /> {p.direccion}</div>}
                  <div className="flex flex-wrap gap-3 text-[11px] text-zinc-400 mt-2">
                    {p.fecha_montaje && <span><Calendar className="w-3 h-3 inline mr-0.5" /> Montaje: <b className="text-zinc-300">{p.fecha_montaje}</b></span>}
                    <span>Firmado por: <b className="text-zinc-300">{p.firmado_por}</b>{p.firmado_dni && <span className="text-zinc-500"> · {p.firmado_dni}</span>}</span>
                    {p.firmado_at && <span className="text-emerald-400">{p.firmado_at.slice(0, 10)}</span>}
                  </div>
                </div>
                <a href={p.pdf_firmado_url} target="_blank" rel="noreferrer" data-testid={`download-parte-${p.peticion_id}`}>
                  <Button size="sm" className="bg-emerald-500 hover:bg-emerald-400 text-zinc-900 font-bold">
                    <Download className="w-3.5 h-3.5 mr-1.5" /> Descargar PDF
                  </Button>
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </PortalSubShell>
  );
}


// ============ NOTIFICACIONES (sugerir cambios prefs) ============
export function PortalSubNotifPrefs() {
  const nav = useNavigate();
  const ctx = getSubContext();
  useEffect(() => {
    if (!ctx?.sub_cliente) nav("/portal-sub/login", { replace: true });
  // eslint-disable-next-line
  }, []);
  if (!ctx?.sub_cliente) return null;
  return (
    <PortalSubShell>
      <div className="max-w-3xl mx-auto px-6 py-8" data-testid="portal-sub-notif-prefs">
        <h1 className="text-2xl font-bold text-zinc-100 mb-1">Mis notificaciones</h1>
        <p className="text-sm text-zinc-400 mb-6">
          Configura cómo y cuándo quieres recibir avisos. Tus cambios quedarán
          pendientes hasta que el equipo de RotulaciónDNF los apruebe.
        </p>
        {/* clienteId no se usa en mode=portal; el backend resuelve el cliente del token */}
        <NotifPrefsPanel clienteId="self" mode="portal" apiClient={subApi} />
      </div>
    </PortalSubShell>
  );
}
